import { Link } from 'react-router-dom';
import {
  useCallback,
  useEffect,
  useRef,
  type AnchorHTMLAttributes,
  type FocusEvent,
  type MouseEvent,
  type Ref,
} from 'react';
import {
  addTrailingSlashToInternalHref,
  isInternalHref,
  prefetchRoute,
} from '../../../shared/routerUtils';

export type AppLinkPrefetch = 'none' | 'intent' | 'viewport';

export type AppLinkProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'href'
> & {
  href: string;
  /** Replace the current history entry instead of pushing a new one. */
  replace?: boolean;
  /**
   * Prefetches the matched lazy route modules before navigation.
   *
   * - `true` / `'viewport'` — when the link enters the viewport (Next.js default)
   * - `'intent'` — on hover or focus
   * - `false` / `'none'` — disabled
   *
   * Prefetching runs in production only.
   */
  prefetch?: boolean | AppLinkPrefetch;
  ref?: Ref<HTMLAnchorElement>;
};

const resolvePrefetchMode = (
  prefetch: AppLinkProps['prefetch'],
): AppLinkPrefetch => {
  if (prefetch === false || prefetch === 'none') {
    return 'none';
  }
  if (prefetch === true || prefetch === undefined) {
    return 'viewport';
  }
  return prefetch;
};

const assignRef = (
  node: HTMLAnchorElement | null,
  ref: Ref<HTMLAnchorElement> | undefined,
): void => {
  if (typeof ref === 'function') {
    ref(node);
    return;
  }
  if (ref) {
    ref.current = node;
  }
};

/**
 * App-wide link, wrapping React Router 7's `<Link>` while preserving the
 * existing `href`-based prop surface used across the app (so call sites are
 * unchanged).
 *
 * - Internal paths get the router basename (`/v2`) applied automatically and
 *   the env-appropriate trailing slash re-applied, matching `useAppRouter`'s
 *   programmatic `push`/`replace` so declarative navigation lands on the same
 *   URL shape the server serves on a full page load.
 * - Absolute/external URLs are rendered as a normal anchor by RR7, so
 *   `target='_blank'` help links keep working.
 * - Lazy route modules are prefetched in production via `prefetchRoute`
 *   (library-mode equivalent of Next.js `<Link prefetch>`).
 */
export const AppLink = ({
  href,
  prefetch,
  ref,
  onMouseEnter,
  onFocus,
  ...props
}: AppLinkProps) => {
  const prefetchMode = resolvePrefetchMode(prefetch);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const prefetchedRef = useRef(false);

  const triggerPrefetch = useCallback(() => {
    if (
      prefetchMode === 'none' ||
      prefetchedRef.current ||
      !isInternalHref(href)
    ) {
      return;
    }

    prefetchedRef.current = true;
    prefetchRoute(href);
  }, [href, prefetchMode]);

  useEffect(() => {
    if (prefetchMode !== 'viewport' || !isInternalHref(href)) {
      return;
    }

    const element = linkRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          triggerPrefetch();
          observer.disconnect();
        }
      },
      { rootMargin: '200px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [href, prefetchMode, triggerPrefetch]);

  const handleMouseEnter = (event: MouseEvent<HTMLAnchorElement>): void => {
    if (prefetchMode === 'intent') {
      triggerPrefetch();
    }
    onMouseEnter?.(event);
  };

  const handleFocus = (event: FocusEvent<HTMLAnchorElement>): void => {
    if (prefetchMode === 'intent') {
      triggerPrefetch();
    }
    onFocus?.(event);
  };

  return (
    <Link
      to={addTrailingSlashToInternalHref(href)}
      ref={(node: HTMLAnchorElement | null) => {
        linkRef.current = node;
        assignRef(node, ref);
      }}
      onMouseEnter={handleMouseEnter}
      onFocus={handleFocus}
      {...props}
    />
  );
};
